import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { AccountSummary } from '../models/account.model';

/**
 * Servicio para manejar el estado global de la aplicación
 * Reemplaza la funcionalidad principal que antes proporcionaba NgRx
 */
@Injectable({
  providedIn: 'root'
})
export class AppStateService {
  // Estado de autenticación
  private isAuthenticatedSubject = new BehaviorSubject<boolean>(false);
  public isAuthenticated$ = this.isAuthenticatedSubject.asObservable();
  
  // Usuario actual
  private currentUserSubject = new BehaviorSubject<AccountSummary | null>(null);
  public currentUser$ = this.currentUserSubject.asObservable();
  
  // Ruta actual
  private currentRouteSubject = new BehaviorSubject<string>('');
  public currentRoute$ = this.currentRouteSubject.asObservable();

  constructor(private router: Router) {
    // Inicializar el estado desde localStorage
    this.checkAuthState();
    
    // Suscribirse a los cambios de ruta
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe((event: any) => {
      this.currentRouteSubject.next(event.url);
      console.log('Route changed:', event.url);
      
      // Verificar el estado de autenticación en cada cambio de ruta
      this.checkAuthState();
    });
  }

  /**
   * Verificar el estado de autenticación - auth_user desde localStorage
   * auth_token ahora está en cookies seguras
   */
  checkAuthState(): void {
    console.log('Checking auth state in AppStateService');
    // Solo leer auth_user de localStorage (auth_token está en cookies)
    const userJson = localStorage.getItem('auth_user');
    
    const isAuthenticated = !!userJson;
    this.isAuthenticatedSubject.next(isAuthenticated);
    
    if (isAuthenticated && userJson) {
      try {
        const user = JSON.parse(userJson);
        this.currentUserSubject.next(user);
        console.log('User authenticated:', user.email);
      } catch (e) {
        console.error('Error parsing user data', e);
        this.clearAuthData();
      }
    } else {
      console.log('User not authenticated');
      this.currentUserSubject.next(null);
    }
  }

  /**
   * Establecer los datos de autenticación
   * auth_token se maneja por cookies, solo guardamos auth_user
   */
  setAuthData(token: string, user: AccountSummary): void {
    // Solo guardar auth_user en localStorage (auth_token va en cookies)
    localStorage.setItem('auth_user', JSON.stringify(user));
    
    this.isAuthenticatedSubject.next(true);
    this.currentUserSubject.next(user);
    
    console.log('Auth data set successfully (user data only)');
  }

  /**
   * Limpiar los datos de autenticación
   * auth_token se limpia por cookies, solo limpiamos auth_user
   */
  clearAuthData(): void {
    // Solo limpiar auth_user de localStorage (auth_token se limpia por cookies)
    localStorage.removeItem('auth_user');
    
    this.isAuthenticatedSubject.next(false);
    this.currentUserSubject.next(null);
    
    console.log('Auth data cleared successfully (user data only)');
  }

  /**
   * Verificar si el usuario está autenticado
   */
  isUserAuthenticated(): boolean {
    return this.isAuthenticatedSubject.value;
  }

  /**
   * Obtener el usuario actual
   */
  getCurrentUser(): AccountSummary | null {
    return this.currentUserSubject.value;
  }

  /**
   * Navegar a una ruta específica
   */
  navigateTo(route: string): void {
    console.log('Navigating to:', route);
    this.router.navigate([route]);
  }
}
